import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Palette, Upload, Loader2, CheckCircle, Image as ImageIcon, Sparkles, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface FirmBrandingSettingsProps {
  accessToken: string;
  firmId?: string;
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Modern, Clean)' },
  { value: 'Roboto', label: 'Roboto (Professional)' },
  { value: 'Open Sans', label: 'Open Sans (Friendly)' },
  { value: 'Lato', label: 'Lato (Classic)' },
  { value: 'Montserrat', label: 'Montserrat (Bold)' },
  { value: 'Poppins', label: 'Poppins (Contemporary)' },
];

const DEFAULT_COLORS = [
  { name: 'Blue', primary: '#1E40AF', secondary: '#3B82F6', accent: '#60A5FA' },
  { name: 'Purple', primary: '#7C3AED', secondary: '#8B5CF6', accent: '#A78BFA' },
  { name: 'Green', primary: '#059669', secondary: '#10B981', accent: '#34D399' },
  { name: 'Red', primary: '#DC2626', secondary: '#EF4444', accent: '#F87171' },
  { name: 'Orange', primary: '#EA580C', secondary: '#F97316', accent: '#FB923C' },
  { name: 'Teal', primary: '#0D9488', secondary: '#14B8A6', accent: '#2DD4BF' },
];

export function FirmBrandingSettings({ accessToken, firmId }: FirmBrandingSettingsProps) {
  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: '#1E40AF',
    secondaryColor: '#3B82F6',
    accentColor: '#60A5FA',
    fontFamily: 'Inter',
    signatureUrl: '',
    // New Fields
    emailSenderName: '',
    emailSenderDomain: '',
    hidePathKitBranding: false
  });
  
  const [currentPlan, setCurrentPlan] = useState<'starter' | 'professional' | 'growth' | 'enterprise'>('starter');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [success, setSuccess] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extractingBranding, setExtractingBranding] = useState(false);

  // Permission Checks
  const canCustomizeBranding = currentPlan !== 'starter';
  const canWhiteLabel = currentPlan === 'enterprise';

  useEffect(() => {
    if (firmId) {
      loadBrandingData();
    }
  }, [firmId]);

  const loadBrandingData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${firmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const firm = data.firm;
        if (firm) {
          if (firm.subscriptionPlan) setCurrentPlan(firm.subscriptionPlan);
          
          setBranding({
            logoUrl: firm.brandingLogoUrl || '',
            primaryColor: firm.brandingPrimaryColor || '#1E40AF',
            secondaryColor: firm.brandingSecondaryColor || '#3B82F6',
            accentColor: firm.brandingAccentColor || '#60A5FA',
            fontFamily: firm.brandingFontFamily || 'Inter',
            signatureUrl: firm.brandingSignatureUrl || '',
            emailSenderName: firm.brandingEmailSenderName || '',
            emailSenderDomain: firm.brandingEmailSenderDomain || '',
            hidePathKitBranding: firm.brandingHidePathKit || false
          });
        }
      }
    } catch (error) {
      console.error('Error loading branding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canCustomizeBranding) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('firmId', firmId || '');
      formData.append('type', 'logo');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/upload-branding`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Failed to upload logo');

      const data = await response.json();
      setBranding({ ...branding, logoUrl: data.url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // ... logic (similar to logo)
    // Signature is allowed for everyone (even Starter) for SOWs usually, but let's assume allowed
    
    setUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('firmId', firmId || '');
      formData.append('type', 'signature');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/upload-branding`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Failed to upload signature');

      const data = await response.json();
      setBranding({ ...branding, signatureUrl: data.url });
      toast.success('Signature uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleSave = async () => {
    if (!firmId) return;

    setSaving(true);
    setSuccess(false);

    try {
      // Construct payload respecting permissions
      const payload = {
          logoUrl: canCustomizeBranding ? branding.logoUrl : undefined,
          primaryColor: canCustomizeBranding ? branding.primaryColor : undefined,
          secondaryColor: canCustomizeBranding ? branding.secondaryColor : undefined,
          accentColor: canCustomizeBranding ? branding.accentColor : undefined,
          fontFamily: canCustomizeBranding ? branding.fontFamily : undefined,
          signatureUrl: branding.signatureUrl, // Always allowed
          emailSenderName: canCustomizeBranding ? branding.emailSenderName : undefined,
          // Enterprise
          hidePathKitBranding: canWhiteLabel ? branding.hidePathKitBranding : undefined,
          emailSenderDomain: canWhiteLabel ? branding.emailSenderDomain : undefined
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/branding`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update branding');
      }

      setSuccess(true);
      toast.success('Branding settings updated successfully');
      
      // Update local styles immediately for "Preview" effect
      if (canCustomizeBranding) {
          document.documentElement.style.setProperty('--color-primary', branding.primaryColor);
          document.documentElement.style.setProperty('--color-accent', branding.accentColor);
      }
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Update branding error:', err);
      toast.error(err.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const applyColorScheme = (scheme: typeof DEFAULT_COLORS[0]) => {
    if (!canCustomizeBranding) return;
    setBranding({
      ...branding,
      primaryColor: scheme.primary,
      secondaryColor: scheme.secondary,
      accentColor: scheme.accent,
    });
  };

  const handleExtractBranding = async () => {
    if (!canCustomizeBranding) {
        toast.error("Upgrade to Pro to use Auto-Import");
        return;
    }
    // ... existing extract logic
    if (!websiteUrl.trim()) return;

    setExtractingBranding(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/extract-website-branding`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ url: websiteUrl }),
        }
      );

      if (!response.ok) throw new Error('Failed to extract');
      const data = await response.json();
      
      setBranding({
        ...branding,
        logoUrl: data.logoUrl || branding.logoUrl,
        primaryColor: data.primaryColor || branding.primaryColor,
        secondaryColor: data.secondaryColor || branding.secondaryColor,
        accentColor: data.accentColor || branding.accentColor,
        fontFamily: data.fontFamily || branding.fontFamily,
      });
      toast.success('Branding extracted');
    } catch (error) {
      toast.error('Failed to extract branding');
    } finally {
      setExtractingBranding(false);
    }
  };

  if (!firmId) return null;

  return (
    <Card className="border-none shadow-none">
      <CardHeader className="px-0 pt-0">
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-600" />
            <div>
                <CardTitle>Branding & White-Labeling</CardTitle>
                <CardDescription>
                Customize your client-facing experience
                </CardDescription>
            </div>
            </div>
            <Badge variant={currentPlan === 'enterprise' ? 'default' : 'secondary'} className="capitalize">
                {currentPlan} Plan
            </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-8 px-0">
        {/* 1. VISUAL IDENTITY (Pro+) */}
        <div className={`space-y-6 ${!canCustomizeBranding ? 'opacity-75 pointer-events-none grayscale-[0.5]' : ''}`}>
             <div className="flex justify-between items-center border-b pb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                    Visual Identity
                    {!canCustomizeBranding && <Badge variant="outline" className="text-xs"><Lock className="size-3 mr-1"/> Locked (Starter)</Badge>}
                </h3>
             </div>

             {/* Quick Import */}
            <Alert className="border-purple-200 bg-purple-50 dark:bg-purple-950">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <AlertDescription className="text-purple-900 dark:text-purple-200">
                <div className="flex gap-2 items-center">
                    <span className="text-sm font-medium whitespace-nowrap">Auto-Import:</span>
                    <Input
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="acme.com"
                      className="h-8 bg-white"
                      disabled={extractingBranding}
                    />
                    <Button 
                      size="sm"
                      onClick={handleExtractBranding}
                      disabled={extractingBranding || !websiteUrl.trim()}
                      className="h-8"
                    >
                      {extractingBranding ? <Loader2 className="size-3 animate-spin"/> : "Go"}
                    </Button>
                </div>
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Logo */}
                <div className="space-y-3">
                  <Label>Company Logo</Label>
                  <div className="flex items-start gap-4">
                    {branding.logoUrl && (
                      <div className="w-24 h-24 border rounded-lg flex items-center justify-center bg-gray-50 p-2">
                        <img src={branding.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1">
                      <Input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} className="text-sm" />
                      <p className="text-[10px] text-gray-500 mt-1">PNG/SVG, max 2MB.</p>
                    </div>
                  </div>
                </div>

                {/* Colors */}
                <div className="space-y-3">
                  <Label>Color Scheme</Label>
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_COLORS.map((scheme) => (
                      <button
                        key={scheme.name}
                        onClick={() => applyColorScheme(scheme)}
                        className="w-8 h-8 rounded-full border shadow-sm hover:scale-110 transition-transform"
                        style={{ background: `linear-gradient(135deg, ${scheme.primary} 50%, ${scheme.secondary} 50%)` }}
                        title={scheme.name}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                            <Label className="text-xs">Primary</Label>
                            <div className="flex gap-1">
                                <Input type="color" value={branding.primaryColor} onChange={(e) => setBranding({...branding, primaryColor: e.target.value})} className="w-8 h-8 p-0 border-none"/>
                                <Input value={branding.primaryColor} onChange={(e) => setBranding({...branding, primaryColor: e.target.value})} className="h-8 text-xs font-mono"/>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Accent</Label>
                            <div className="flex gap-1">
                                <Input type="color" value={branding.accentColor} onChange={(e) => setBranding({...branding, accentColor: e.target.value})} className="w-8 h-8 p-0 border-none"/>
                                <Input value={branding.accentColor} onChange={(e) => setBranding({...branding, accentColor: e.target.value})} className="h-8 text-xs font-mono"/>
                            </div>
                        </div>
                  </div>
                </div>
            </div>
            
            <div className="space-y-3">
              <Label>Font Family</Label>
              <Select value={branding.fontFamily} onValueChange={(v) => setBranding({ ...branding, fontFamily: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((font) => <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
        </div>

        <Separator />

        {/* 2. EMAIL IDENTITY (Pro+) */}
        <div className={`space-y-6 ${!canCustomizeBranding ? 'opacity-75 pointer-events-none grayscale-[0.5]' : ''}`}>
             <div className="flex justify-between items-center border-b pb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">Email Identity</h3>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label>Sender Name</Label>
                    <Input 
                        value={branding.emailSenderName} 
                        onChange={(e) => setBranding({...branding, emailSenderName: e.target.value})}
                        placeholder="e.g. Acme Team"
                    />
                    <p className="text-xs text-muted-foreground">Appears as the "From" name in client emails.</p>
                </div>
             </div>
        </div>

        <Separator />

        {/* 3. WHITE LABELING (Enterprise) */}
        <div className={`space-y-6 rounded-lg p-4 border ${canWhiteLabel ? 'bg-purple-50/20 border-purple-100' : 'bg-gray-50 border-gray-100 opacity-75'}`}>
             <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-purple-900">
                    Enterprise White-Labeling
                    {!canWhiteLabel && <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 ml-2">Upgrade Required</Badge>}
                </h3>
                {!canWhiteLabel && <Lock className="size-5 text-gray-400"/>}
             </div>
             
             <div className="space-y-4">
                 <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <Label>Hide "Powered by PathKit"</Label>
                        <p className="text-xs text-muted-foreground">Remove platform attribution from footer and login.</p>
                    </div>
                    <Switch 
                        checked={branding.hidePathKitBranding}
                        onCheckedChange={(c) => setBranding({...branding, hidePathKitBranding: c})}
                        disabled={!canWhiteLabel}
                    />
                 </div>
                 
                 <div className="space-y-2">
                    <Label>Custom Email Domain</Label>
                    <div className="flex gap-2">
                        <Input 
                            value={branding.emailSenderDomain} 
                            onChange={(e) => setBranding({...branding, emailSenderDomain: e.target.value})}
                            disabled={!canWhiteLabel}
                            placeholder="projects.acme.com"
                        />
                        {canWhiteLabel && branding.emailSenderDomain && <Button variant="outline" size="sm">Verify</Button>}
                    </div>
                 </div>
             </div>
        </div>

        {/* Signature (Always Allowed) */}
        <div className="space-y-3 pt-4 border-t">
              <Label>Signature Image (Optional)</Label>
              <div className="flex items-start gap-4">
                {branding.signatureUrl && (
                  <div className="w-32 h-16 border rounded bg-white p-1 flex items-center justify-center">
                    <img src={branding.signatureUrl} alt="Signature" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <Input type="file" accept="image/*" onChange={handleSignatureUpload} disabled={uploadingSignature} className="text-sm" />
                  <p className="text-[10px] text-gray-500 mt-1">For SOWs (max 1MB)</p>
                </div>
              </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t sticky bottom-0 bg-white p-4 -mx-4 -mb-4 shadow-t">
              <Button onClick={handleSave} disabled={saving || (!canCustomizeBranding && !canWhiteLabel)}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Branding Settings
                  </>
                )}
              </Button>
        </div>
      </CardContent>
    </Card>
  );
}